import { ICalendarDisplaySettingsModel } from "@/Appointments/Models/UI/ICalendarDisplaySettingsModel";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";

import "./CalendarDisplaySettings.css";

interface ICalendarDisplaySettingsProps {
    Model: ICalendarDisplaySettingsModel;
}

class CalendarDisplaySettingsState {
    public maximumCalendarsShownForDay: number;
    public maximumCalendarsShownForWeek: number;
    public maximumCalendarsShownForMonth: number;
}

class CalendarDisplaySettings extends BaseReactComponent<ICalendarDisplaySettingsProps, CalendarDisplaySettingsState> {
    private readonly invalidClassName = "form-control invalid";
    private readonly validClassName = "form-control";

    constructor(props: ICalendarDisplaySettingsProps) {
        super(props);

        const initialState = new CalendarDisplaySettingsState();
        initialState.maximumCalendarsShownForDay = props.Model.get("MaximumCalendarsShownForDay") || calendarStore.calendarsShownDefault;
        initialState.maximumCalendarsShownForWeek = props.Model.get("MaximumCalendarsShownForWeek") || calendarStore.calendarsShownDefault;
        initialState.maximumCalendarsShownForMonth = props.Model.get("MaximumCalendarsShownForMonth") || calendarStore.calendarsShownDefault;

        this.state = initialState;
    }

    public componentDidMount(): void {
        super.setOverlay("CalendarDisplaySettings", { transparent: true });
    }

    public render(): JSX.Element {
        const excludedSymbols = ["+", "-", ".", "E", "e"];
        return (
            <div>
                <table className="f-content">
                    <thead>
                        <tr>
                            {this.props.Model.get("Sites").count() > 1 && (
                                <th className="first" style={{ width: "250px" }}>
                                    <CheckboxControl
                                        id="sites_check"
                                        label={cacheStore.GetString("SitesLabel").toUpperCase()}
                                        operation={LocalOperations.Calendar.Checkbox_Change}
                                        checked={this.props.Model.get("IsAllSitesChecked")}
                                        target={CheckboxTarget.Myle_Calendar_SearchSites}
                                        DivWrap={true}
                                    />
                                </th>
                            )}

                            <th className="first">
                                <CheckboxControl
                                    id="employees_check"
                                    label={cacheStore.GetString("EmployeesLabel").toUpperCase()}
                                    operation={LocalOperations.Calendar.Checkbox_Change}
                                    checked={this.props.Model.get("IsAllEmployeesChecked")}
                                    target={CheckboxTarget.Myle_Calendar_SearchEmployees}
                                    DivWrap={true}
                                />
                            </th>

                            <th className="first" style={{ width: "150px" }}>
                                <label>{cacheStore.GetString("AppointmentsLabel").toUpperCase()}</label>
                            </th>

                            <th className="last" style={{ width: "350px" }}>
                                <label>{cacheStore.GetString("CalendarSettingsLabel").toUpperCase()}</label>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {this.props.Model.get("Sites").count() > 1 && (
                                <td className="first" style={{ width: "250px" }}>
                                    <div style={{ height: "400px", overflow: "auto" }}>
                                        <CheckedListControl
                                            DataSource={this.props.Model.get("Sites")}
                                            Operation={LocalOperations.Calendar.Checkbox_Change}
                                            limitTextWidth={false}
                                        />
                                    </div>
                                </td>
                            )}

                            <td className="first" style={{ width: "300px" }}>
                                <input
                                    id="__emps32433"
                                    className="form-control"
                                    type="text"
                                    value={this.props.Model.get("EmployeesSearchText")}
                                    onChange={e => this.onEmployeeFilterChange(e)}
                                    placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                                />
                                <div style={{ height: "400px", overflow: "auto" }}>
                                    <CheckedListControl
                                        dataCy="calendarDisplaySettings-employees-checklist"
                                        DataSource={this.props.Model.get("EmployeesFiltered")}
                                        Operation={LocalOperations.Calendar.Checkbox_Change}
                                        limitTextWidth={false}
                                        sort={true}
                                    />
                                </div>
                            </td>

                            <td className="first">
                                <CheckboxControl
                                    id="patientsCheck"
                                    dataCy="calendarDisplaySettings-showPatientName-checkbox"
                                    checked={this.props.Model.get("PatientsAreVisible")}
                                    label={cacheStore.GetString("PatientsNamesLabel")}
                                    operation={LocalOperations.Calendar.Checkbox_Change}
                                    target={CheckboxTarget.Myle_Calendar_PatientsAreVisible}
                                    DivWrap
                                />
                                <CheckboxControl
                                    id="roomCheck"
                                    dataCy="calendarDisplaySettings-showRoom-checkbox"
                                    checked={this.props.Model.get("RoomsAreVisible")}
                                    label={cacheStore.GetString("RoomLabel")}
                                    operation={LocalOperations.Calendar.Checkbox_Change}
                                    target={CheckboxTarget.Myle_Calendar_RoomsAreVisible}
                                    DivWrap
                                />
                                <CheckboxControl
                                    checked={this.props.Model.get("StatusAreVisible")}
                                    dataCy="calendarDisplaySettings-showStatus-checkbox"
                                    label={cacheStore.GetString("StatusLabel")}
                                    operation={LocalOperations.Calendar.Checkbox_Change}
                                    target={CheckboxTarget.Myle_Calendar_StatusAreVisible}
                                    DivWrap
                                />
                                <CheckboxControl
                                    id="telephoneCheck"
                                    dataCy="calendarDisplaySettings-showTelephone-checkbox"
                                    checked={this.props.Model.get("ContactPointsAreVisible")}
                                    label={cacheStore.GetString("TelephoneLabel")}
                                    operation={LocalOperations.Calendar.Checkbox_Change}
                                    target={CheckboxTarget.Myle_Calendar_PhonesAreVisible}
                                    DivWrap
                                />
                                <CheckboxControl
                                    id="namCheck"
                                    dataCy="calendarDisplaySettings-showNAM-checkbox"
                                    checked={this.props.Model.get("IncludeNAM")}
                                    label={sessionStore.getDefaultHealthCardLabel()}
                                    operation={LocalOperations.Calendar.Checkbox_Change}
                                    target={CheckboxTarget.Myle_Calendar_IncludeNAM}
                                    DivWrap
                                />
                                <CheckboxControl
                                    id="includeSitesCheck"
                                    dataCy="calendarDisplaySettings-showSites-checkbox"
                                    checked={this.props.Model.get("SitesAreVisible")}
                                    label={cacheStore.GetString("SitesLabel")}
                                    operation={LocalOperations.Calendar.Checkbox_Change}
                                    target={CheckboxTarget.Myle_Calendar_SitesAreVisible}
                                    DivWrap
                                />
                                {cacheStore.ClinicSettings.EnableCanadianTriageAndAcuityScore && (
                                    <CheckboxControl
                                        id="CanadianTriageAndAcuityScoreCheck"
                                        checked={this.props.Model.get("CanadianTriageAndAcuityScoreVisible")}
                                        label={cacheStore.GetString("TriageLabel")}
                                        operation={LocalOperations.Calendar.Checkbox_Change}
                                        target={CheckboxTarget.Myle_Calendar_TriageScoreVisible}
                                        DivWrap
                                    />
                                )}
                                <CheckboxControl
                                    id="oneLineViewCheck"
                                    checked={this.props.Model.get("OneLineViewSelected")}
                                    label={cacheStore.GetString("CalendarSettings_SubjectOnFirstLineLabel")}
                                    operation={LocalOperations.Calendar.Checkbox_Change}
                                    target={CheckboxTarget.Myle_Calendar_OneLineViewSelected}
                                    DivWrap
                                />
                            </td>

                            <td className="last" style={{ minWidth: 420, maxWidth: 450, overflow: "hidden" }}>
                                <CheckboxControl
                                    id="includeCancelledCheck"
                                    checked={this.props.Model.get("IncludeCanceled")}
                                    label={cacheStore.GetString("CalendarSettings_IncludeCanceledAppointmentsLabel")}
                                    operation={LocalOperations.Calendar.Checkbox_Change}
                                    target={CheckboxTarget.Myle_Calendar_IncludeCanceled}
                                    DivWrap
                                />
                                <CheckboxControl
                                    id="collapseRuleSetsCheck"
                                    checked={this.props.Model.get("AreRulesetsCollapsed")}
                                    label={cacheStore.GetString("CalendarSettings_CollapseRulesetLabel")}
                                    operation={LocalOperations.Calendar.Checkbox_Change}
                                    target={CheckboxTarget.Myle_Calendar_CollapseRulesets}
                                    DivWrap
                                />

                                <CheckboxControl
                                    id="sites_onlySelected"
                                    label={cacheStore.GetString("CalendarSettings_ShowOnlySelectedSitesAppointmentsLabel")}
                                    operation={LocalOperations.Calendar.Checkbox_Change}
                                    checked={this.props.Model.get("ShowOnlySelectedSites")}
                                    target={CheckboxTarget.Myle_Calendar_OnlySelectedSites}
                                    DivWrap
                                />

                                {cacheStore.ClinicSettings.EnableDynamicRosterCalendarSorting && (
                                    <CheckboxControl
                                        id="employees_seeingPatientsFirst"
                                        label={cacheStore.GetString("CalendarSettings_SortByScheduleSelectedEmployeesLabel")}
                                        operation={LocalOperations.Calendar.Checkbox_Change}
                                        checked={this.props.Model.get("SortByScheduleSelectedEmployees")}
                                        target={CheckboxTarget.Myle_Calendar_SortByScheduleSelectedEmployees}
                                        DivWrap
                                    />
                                )}

                                <div className="clearfix" style={{ margin: "10px 0px 15px" }}>
                                    <span className="pull-left" style={{ margin: "6px 7px 0 0", textAlign: "center" }}>{cacheStore.GetString("CalendarSettings_DisplayIntervalLabel")}</span>
                                    <div style={{ width: "100px", float: "left", marginLeft: "20px" }}>
                                        <select className="form-control" onChange={e => this.onCalendarTimeResolutionChange(e)} value={this.props.Model.get("TimeSlotLength").toString()}>
                                            {this.props.Model.get("CalendarTimeResolutions").map(item => (
                                                <option key={item + "_spo"} value={item.toString()}>{item} {cacheStore.GetString("CalendarSettings_MinLabel")}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <label>
                                    {cacheStore.GetString("CalendarSettings_SetMaximumShownLabel")}
                                </label>
                                <div className="max-calendar-shown">
                                    <div className="max-calendar-shown-items">
                                        <label>
                                            {cacheStore.GetString("DayLabel")}
                                        </label>
                                        <input
                                            className={this.state.maximumCalendarsShownForDay ? this.validClassName : this.invalidClassName}
                                            style={{ width: "100px" }}
                                            type="number"
                                            min={calendarStore.calendarsShownMin}
                                            max={calendarStore.calendarsShownMax}
                                            onKeyDown={e => excludedSymbols.includes(e.key) && e.preventDefault()}
                                            value={this.state.maximumCalendarsShownForDay}
                                            onBlur={e => this.setDefaultForDayChange(e)}
                                            onChange={e => this.onMaximumCalendarsShownForDayChange(e)}
                                        />
                                    </div>
                                    <div className="max-calendar-shown-items">
                                        <label>
                                            {cacheStore.GetString("WeekLabel")}
                                        </label>
                                        <input
                                            className={this.state.maximumCalendarsShownForWeek ? this.validClassName : this.invalidClassName}
                                            style={{ width: "100px" }}
                                            type="number"
                                            min={calendarStore.calendarsShownMin}
                                            max={calendarStore.calendarsShownMax}
                                            onBlur={e => this.setDefaultForWeekChange(e)}
                                            onKeyDown={e => excludedSymbols.includes(e.key) && e.preventDefault()}
                                            value={this.state.maximumCalendarsShownForWeek}
                                            onChange={e => this.onMaximumCalendarsShownForWeekChange(e)}
                                        />
                                    </div>
                                    <div className="max-calendar-shown-items">
                                        <label>
                                            {cacheStore.GetString("MonthLabel")}
                                        </label>
                                        <input
                                            className={this.state.maximumCalendarsShownForMonth ? this.validClassName : this.invalidClassName}
                                            style={{ width: "100px" }}
                                            type="number"
                                            min={calendarStore.calendarsShownMin}
                                            max={calendarStore.calendarsShownMax}
                                            onBlur={e => this.setDefaultForMonthChange(e)}
                                            onKeyDown={e => excludedSymbols.includes(e.key) && e.preventDefault()}
                                            value={this.state.maximumCalendarsShownForMonth}
                                            onChange={e => this.onMaximumCalendarsShownForMonthChange(e)}
                                        />
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div className="f-footer calendar-footer-actions">
                    <CheckboxControl
                        id="saveSettingsChecked"
                        checked={this.props.Model.get("SaveSettingsSelected")}
                        label={cacheStore.GetString("RememberUserSettings")}
                        operation={LocalOperations.Calendar.Checkbox_Change}
                        target={CheckboxTarget.Myle_Calendar_SaveSettingsSelected}
                        DivWrap
                    />
                    <ButtonGroup>
                        <ButtonControl
                            variant="neutral"
                            onClick={() => this.onCancelButtonClick()}
                            label={cacheStore.GetString("CancelLabel")}
                        />
                        <ButtonControl
                            variant="brand"
                            onClick={() => this.onOkButtonClick()}
                            label={cacheStore.GetString("Ok_Label")}
                            dataCy="calendarDisplaySettings-ok-button"
                        />
                    </ButtonGroup>
                </div>
            </div>
        );
    }

    private onMaximumCalendarsShownForDayChange(e: React.ChangeEvent<HTMLInputElement>): void {
        const value = e.target.value;
        this.onMaximumCalendarsShownChange(
            Number(value),
            LocalOperations.Calendar.DisplaySettings_ChangeMaximumCalendarsShownForDay,
            s => s.maximumCalendarsShownForDay = value ? Number(value) : null
        );
    }

    private onMaximumCalendarsShownForWeekChange(e: React.ChangeEvent<HTMLInputElement>): void {
        const value = e.target.value;
        this.onMaximumCalendarsShownChange(
            Number(value),
            LocalOperations.Calendar.DisplaySettings_ChangeMaximumCalendarsShownForWeek,
            s => s.maximumCalendarsShownForWeek = value ? Number(value) : null
        );
    }

    private onMaximumCalendarsShownForMonthChange(e: React.ChangeEvent<HTMLInputElement>): void {
        const value = e.target.value;
        this.onMaximumCalendarsShownChange(
            Number(value),
            LocalOperations.Calendar.DisplaySettings_ChangeMaximumCalendarsShownForMonth,
            s => s.maximumCalendarsShownForMonth = value ? Number(value) : null
        );
    }

    private onMaximumCalendarsShownChange(value: number, operation: number, mutation: (s: CalendarDisplaySettingsState) => void): void {
        this.mutateState(mutation, () => {
            sessionStore.dispatch(
                operation,
                { value: value || calendarStore.calendarsShownDefault }
            );
        });
    }

    private setDefaultForDayChange(e: React.FocusEvent<HTMLInputElement, Element>): void {
        this.setDefaultIfInvalid(
            e,
            LocalOperations.Calendar.DisplaySettings_ChangeMaximumCalendarsShownForDay,
            (s, value) => s.maximumCalendarsShownForDay = value
        );
    }

    private setDefaultForWeekChange(e: React.FocusEvent<HTMLInputElement, Element>): void {
        this.setDefaultIfInvalid(
            e,
            LocalOperations.Calendar.DisplaySettings_ChangeMaximumCalendarsShownForWeek,
            (s, value) => s.maximumCalendarsShownForWeek = value
        );
    }

    private setDefaultForMonthChange(e: React.FocusEvent<HTMLInputElement, Element>): void {
        this.setDefaultIfInvalid(
            e,
            LocalOperations.Calendar.DisplaySettings_ChangeMaximumCalendarsShownForMonth,
            (s, value) => s.maximumCalendarsShownForMonth = value
        );
    }

    private setDefaultIfInvalid(e: React.FocusEvent<HTMLInputElement, Element>, operation: number, mutation: (s: CalendarDisplaySettingsState, value: number) => void): void {
        if (!e.target.value)
            return;

        let value = Number(e.target.value);
        if (value >= calendarStore.calendarsShownMin && value <= calendarStore.calendarsShownMax)
            return;

        e.target.value = String(calendarStore.calendarsShownDefault);
        e.target.className = this.validClassName;
        value = calendarStore.calendarsShownDefault;

        this.mutateState(s => mutation(s, value), () => sessionStore.dispatch(operation, { value: value }));
    }

    private onCancelButtonClick(): void {
        sessionStore.dispatch(LocalOperations.Calendar.DisplaySettings_Cancel);
    }

    private onOkButtonClick(): void {
        sessionStore.dispatch(LocalOperations.Calendar.DisplaySettings_Ok);
    }

    private onCalendarTimeResolutionChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        sessionStore.dispatch(
            LocalOperations.Calendar.DisplaySettings_ChangeTimeResolution,
            { value: e.target.value }
        );
    }

    private onEmployeeFilterChange(event: React.ChangeEvent<HTMLInputElement>): void {
        sessionStore.dispatch(
            LocalOperations.Calendar.DisplaySettings_EmployeeFilterChange,
            { value: event.target.value }
        );
    }
}

export { CalendarDisplaySettings };
